import { useEffect, useRef, useState } from "react";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { customerService } from "../../services/CustomerService";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { FilterMatchMode } from "primereact/api";
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { exportToExcel } from "../../utils/excelExportHelper";

export default function CustomerListPage() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const searchInputRef = useRef(null);
  const { user } = useAuth();
  const isSalesUser = user?.UserRole === "Sales";
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const savedSearch = localStorage.getItem("customer_search_query") || "";
  const [globalFilterValue, setGlobalFilterValue] = useState(savedSearch);
  const [filters, setFilters] = useState({
    global: { value: savedSearch || null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    phone: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const navigate = useNavigate();
  const toast = useRef(null);

  const handleExport = () => {
    const headers = [
      { label: "Müşteri Adı", key: "name" },
      { label: "Telefon", key: "phone" },
      { label: "E-posta", key: "email" },
      { label: "TCKN / Vergi No", key: "taxNumber" },
      { label: "Vergi Dairesi", key: "taxOffice" },
      { label: "Bakiye (TL)", key: "balanceFormatted" },
      { label: "Banka Bilgisi", key: "bankInfo" }
    ];

    const dataToExport = customers.map(c => ({
      ...c,
      balanceFormatted: (c.balance || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })
    }));

    exportToExcel(dataToExport, headers, "musteri_listesi");
  };

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const res = await customerService.list();
      setCustomers(res.data);
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Müşteri listesi yüklenirken bir hata oluştu.",
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  }, []);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
    localStorage.setItem("customer_search_query", value);
  };

  const balanceTemplate = (rowData) => {
    const balance = rowData.balance || 0;
    const isNegative = balance < 0;
    return (
      <span className={`font-bold ${isNegative ? "text-red-500" : "text-green-600"}`}>
        {balance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
      </span>
    );
  };

  const handleDeleteCustomer = (row) => {
    const hasOpeningBalance = row.openingBalance && row.openingBalance !== 0;

    confirmDialog({
      message: hasOpeningBalance
        ? `${row.name} isimli müşterinin açılış bakiyesi mevcut. Yine de silmek istiyor musunuz?`
        : "Müşteri silinecektir Emin misiniz?",
      header: "Müşteri Sil",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Evet",
      rejectLabel: "Hayır",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await customerService.delete(row.id);
          toast.current?.show({
            severity: "success",
            summary: "Başarılı",
            detail: "Müşteri başarıyla silindi.",
            life: 3000
          });
          loadCustomers();
        } catch (err) {
          console.error(err);
          toast.current?.show({
            severity: "error",
            summary: "Hata",
            detail: err.response?.data?.message || "Müşteri silinirken bir hata oluştu.",
            life: 3000
          });
        }
      }
    });
  };

  const actionsTemplate = (row) => (
    <div className="flex gap-1 justify-content-center">
      {!isSalesUser && (
        <>
          <Button
            icon="pi pi-pencil"
            rounded
            text
            severity="info"
            tooltip="Düzenle"
            className="w-8 h-8"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/customers/edit/${row.id}`);
            }}
          />
          {!row.hasTransactions && (
            <Button
              icon="pi pi-trash"
              rounded
              text
              severity="danger"
              tooltip="Sil"
              className="w-8 h-8"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteCustomer(row);
              }}
            />
          )}
        </>
      )}
      <Button
        icon="pi pi-eye"
        rounded
        text
        severity="help"
        tooltip="Detay"
        className="w-8 h-8"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/customers/detail/${row.id}`);
        }}
      />
    </div>
  );

  const alacakToplam = customers.reduce((sum, c) => (c.balance || 0) > 0 ? sum + c.balance : sum, 0);
  const verecekToplam = customers.reduce((sum, c) => (c.balance || 0) < 0 ? sum + Math.abs(c.balance) : sum, 0);
  const netToplam = alacakToplam - verecekToplam;

  const formatCurrency = (value) => {
    return (value ?? 0).toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " ₺";
  };

  const header = (
    <div className="flex flex-column md:flex-row justify-content-between align-items-center gap-2 py-1">
      <div className="flex flex-column md:flex-row gap-2 align-items-center w-full md:w-auto">
        <IconField iconPosition="left" className="w-full md:w-48">
          <InputIcon className="pi pi-search text-sm"> </InputIcon>
          <InputText
            ref={searchInputRef}
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Ara..."
            className="p-inputtext-sm border-round-lg w-full"
          />
        </IconField>
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Temizle"
          className="p-button-outlined p-button-secondary p-button-sm rounded-xl ml-3"
          onClick={() => {
            setFilters({
              global: { value: null, matchMode: FilterMatchMode.CONTAINS },
              name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
              email: { value: null, matchMode: FilterMatchMode.CONTAINS },
              phone: { value: null, matchMode: FilterMatchMode.CONTAINS },
            });
            setGlobalFilterValue("");
            localStorage.removeItem("customer_search_query");
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="bg-[#f8fafc] min-h-screen animate-fade-in">
      <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

      <div className="bg-white shadow-lg shadow-slate-200/50 border border-slate-200 rounded-2xl overflow-hidden">

        <ConfirmDialog />
        <div className="!bg-slate-200 dark:!bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-4 md:px-6 md:py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="!bg-blue-600 p-4 rounded-xl shadow-md shadow-blue-500/20">
              <i className="pi pi-users text-white text-lg"></i>
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Müşteri Listesi</h1>
              <p className="text-slate-500 dark:text-slate-400 text-xs font-bold italic">Müşteri ve Hesap Yönetimi</p>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button
              label="EXCEL'DEN İÇE AKTAR"
              icon="pi pi-file-excel"
              className="!bg-emerald-600 border-none hover:!bg-emerald-700 px-5 py-2.5 rounded-xl font-black text-xs shadow-lg shadow-emerald-500/20 transition-all active:scale-95"
              onClick={() => navigate("/customers/import")}
            />
            <Button
              label="VERİLERİ DIŞARI AKTAR"
              icon="pi pi-file-export"
              className="!bg-slate-700 border-none hover:!bg-slate-800 px-5 py-2.5 rounded-xl font-black text-xs shadow-lg shadow-slate-500/20 transition-all active:scale-95"
              onClick={handleExport}
            />
            <Button
              label="YENİ MÜŞTERİ EKLE"
              icon="pi pi-plus"
              className="!bg-blue-600 border-none hover:!bg-blue-700 px-5 py-2.5 rounded-xl font-black text-xs shadow-lg shadow-blue-500/20 transition-all active:scale-95"
              onClick={() => navigate("/customers/add")}
            />
          </div>
        </div>

        {/* Bilgi Kartları */}
        <div className="p-4 bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-4 bg-blue-600 rounded-full"></div>
            </div>

            <button
              type="button"
              onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
              className="
                group
                !w-8 !h-8
                flex-shrink-0
                rounded-xl
                bg-slate-200/60 hover:bg-slate-300/80
                dark:bg-slate-800 dark:hover:bg-slate-700
                text-slate-600 dark:text-slate-300
                hover:text-slate-800 dark:hover:text-white
                active:scale-95
                transition-all duration-300
                flex items-center justify-center border-none
              "
              title={isSummaryExpanded ? "Özeti Gizle" : "Özeti Göster"}
            >
              <i className={`pi pi-chevron-down text-[12px] transition-transform duration-300 ${isSummaryExpanded ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {isSummaryExpanded && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 animate-fade-in">
              <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200/80 dark:border-slate-700/80 shadow-md flex items-center justify-between">
                <div>
                  <p className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">Alacak Toplamı</p>
                  <h3 className="text-xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight">
                    {formatCurrency(alacakToplam)}
                  </h3>
                </div>
                <div className="w-2 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 flex-shrink-0">
                  <i className="pi pi-arrow-up-right text-sm" />
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200/80 dark:border-slate-700/80 shadow-md flex items-center justify-between">
                <div>
                  <p className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">Verecek Toplamı</p>
                  <h3 className="text-xl font-black text-rose-600 dark:text-rose-400 tracking-tight">
                    {formatCurrency(verecekToplam)}
                  </h3>
                </div>
                <div className="w-2 h-8 rounded-lg bg-rose-50 dark:bg-rose-950/30 flex items-center justify-center text-rose-600 dark:text-rose-400 flex-shrink-0">
                  <i className="pi pi-arrow-down-left text-sm" />
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200/80 dark:border-slate-700/80 shadow-md flex items-center justify-between">
                <div>
                  <p className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">Net Toplam</p>
                  <h3 className={`text-xl font-black tracking-tight ${netToplam >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                    {formatCurrency(netToplam)}
                  </h3>
                </div>
                <div className={`w-2 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${netToplam >= 0 ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400" : "bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400"}`}>
                  <i className="pi pi-calculator text-sm" />
                </div>
              </div>
            </div>
          )}
        </div>

        <div >
          <DataTable
            value={customers}
            paginator
            rows={15}
            rowsPerPageOptions={[15, 25, 50]}
            loading={loading}
            filters={filters}
            globalFilterFields={["name", "email", "phone"]}
            header={header}
            stripedRows
            rowHover
            showGridlines
            responsiveLayout="stack"
            breakpoint="960px"
            onRowDoubleClick={(e) => navigate(`/customers/detail/${e.data.id}`)}
            className="p-datatable-sm"
            paginatorClassName="  "
          >
            <Column
              field="customerNumber"
              header="No"
              sortable
              body={(r) => <Tag value={r.customerNumber || 'N/A'} severity="info" />}
              style={{ width: '8%' }}
            />

            <Column
              field="name"
              header="Müşteri Adı / Ünvan"
              sortable
              className="font-bold text-900"
              style={{ width: '20%' }}
            />
            <Column
              field="branchName"
              header="Şube Yetkisi"
              sortable
              style={{ width: '12%' }}
              body={(row) => (
                <span className={`font-bold text-xs px-2.5 py-1 rounded-md ${row.branchId ? 'text-blue-600 bg-blue-50' : 'text-slate-600 bg-slate-50'}`}>
                  {row.branchName || "Global"}
                </span>
              )}
            />
            <Column
              field="note"
              header="Not"
              style={{ width: '15%' }}
              body={(row) => (
                <span className="text-xs text-slate-500 italic">
                  {row.note || "-"}
                </span>
              )}
            />
            <Column
              header="Yaklaşan Ödeme"
              sortable
              field="nextDueDate"
              body={(row) => (
                <div className="flex flex-col">
                  {row.nextDueDate ? (
                    <>
                      <span className="text-xs font-black text-orange-600">
                        {new Date(row.nextDueDate).toLocaleDateString("tr-TR")}
                      </span>
                      <span className="text-[10px] font-bold text-slate-400">
                        ₺{row.nextDueAmount?.toLocaleString("tr-TR")}
                      </span>
                    </>
                  ) : (
                    <span className="text-slate-300">-</span>
                  )}
                </div>
              )}
              style={{ width: '15%' }}
            />
            <Column field="phone" header="Telefon" style={{ width: '15%' }} />
            <Column
              field="balance"
              header="Güncel Bakiye"
              sortable
              body={balanceTemplate}
              style={{ width: '15%' }}
              align="right"
            />
            <Column
              header="Durum"
              style={{ width: '10%' }}
              align="center"
              body={(row) => (
                <Tag
                  value={row.isActive ? "AKTİF" : "PASİF"}
                  severity={row.isActive ? "success" : "secondary"}
                  className="rounded-lg font-black text-[10px] px-3 py-1 border-none"
                  style={{
                    backgroundColor: row.isActive ? '#dcfce7' : '#f1f5f9',
                    color: row.isActive ? '#166534' : '#64748b'
                  }}
                />
              )}
            />
            <Column body={actionsTemplate} header="İşlemler" style={{ width: '10%' }} align="center" />
          </DataTable>
        </div>
      </div>
    </div>
  );
}
